import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';     
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications, Notification } from '../../hooks/useNotifications';
import { BellIcon, XMarkIcon, CheckIcon, TrashIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import { formatRelativeTime, formatCairoTime } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ 
  isOpen, 
  onClose, 
  className = '' 
}) => {
  const { t, i18n } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    loading, 
    fetchNotifications, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    dropdownNotifications,
    dropdownLoading,
    hasMoreDropdown,
    fetchDropdownNotifications,
    loadMoreDropdownNotifications
  } = useNotifications();
  
  const [showAll, setShowAll] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchDropdownNotifications(1, false); // Reset and fetch first 5
    }
  }, [isOpen, fetchDropdownNotifications]);

  // Refetch on window focus/visibility when dropdown is open (near-realtime)
  useEffect(() => {
    if (!isOpen) return;
    const onFocus = () => fetchDropdownNotifications(1, false);
    const onVisibility = () => { if (!document.hidden) fetchDropdownNotifications(1, false); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isOpen, fetchDropdownNotifications]);

  // Mark visible notifications as read when dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    const unreadVisible = dropdownNotifications.filter(n => !n.isRead);
    // Mark only what's visible (max 5 per page)
    unreadVisible.forEach(n => {
      void markAsRead(n.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dropdownNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const getNotificationTitle = (notification: Notification) => {
    return i18n.language.startsWith('ar') ? notification.titleAr : notification.titleEn;
  };

  const getNotificationMessage = (notification: Notification) => {
    return i18n.language.startsWith('ar') ? notification.messageAr : notification.messageEn;
  };

  const locale = currentLanguage === 'ar' ? 'ar' : 'en';

  const formatTimeAgo = (dateString: string) => {
    return formatRelativeTime(dateString, locale, t);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    
    if (notification.redirectUrl) {
      if (notification.type === 'support' && notification.redirectUrl === '/support') {
        navigate('/support-messages');
      } else {
        navigate(notification.redirectUrl);
      }
    }
    
    onClose();
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    await deleteNotification(notificationId);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500 bg-red-50';
      case 'high':
        return 'border-l-orange-500 bg-orange-50';
      case 'normal':
        return 'border-l-blue-500 bg-blue-50';
      case 'low':
        return 'border-l-gray-500 bg-gray-50';
      default:
        return 'border-l-blue-500 bg-blue-50';
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={dropdownRef}
      className={
        `absolute top-full mt-2 w-[min(92vw,24rem)] sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 ${className} ` +
        // Mobile: center under bell
        `left-1/2 -translate-x-1/2 ` +
        // Desktop/tablet: align to edge near bell, reset translate
        (isRTL ? 'md:left-0 md:right-auto ' : 'md:right-0 md:left-auto ') +
        'md:translate-x-0'
      }
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          {unreadCount > 0 ? (
            <BellIconSolid className="h-5 w-5 text-emerald-600" />
          ) : (
            <BellIcon className="h-5 w-5 text-gray-600" />
          )}
          <h3 className="text-lg font-semibold text-gray-900">
            {t('notifications.title', 'Notifications')}
          </h3>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              {t('notifications.markAllRead', 'Mark all read')}
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* View All */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <button
          onClick={() => {
            const dest = user?.role === 'admin' ? '/admin/notifications' : '/notifications';
            navigate(dest);
            onClose();
          }}
          className="w-full text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center justify-center"
        >
          {t('notifications.viewAll', 'View all notifications')}
        </button>
      </div>

      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        {dropdownLoading && dropdownNotifications.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {t('notifications.loading', 'Loading notifications...')}
          </div>
        ) : dropdownNotifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <BellIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>{t('notifications.empty', 'No notifications yet')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {dropdownNotifications.map((notification: Notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 border-l-4 ${
                  notification.isRead ? 'bg-white' : 'bg-blue-50'
                } ${getPriorityColor(notification.priority)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className={`text-sm font-medium ${
                        notification.isRead ? 'text-gray-700' : 'text-gray-900'
                      }`}>
                        {getNotificationTitle(notification)}
                      </h4>
                      {!notification.isRead && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {getNotificationMessage(notification)}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {formatTimeAgo(notification.createdAtUtc)}
                      </span>
                      <div className="flex items-center space-x-2">
                        {notification.type && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {t(`notifications.types.${notification.type}`, notification.type)}
                          </span>
                        )}
                        <button
                          onClick={(e) => handleDeleteNotification(e, notification.id)}
                          className="text-gray-400 hover:text-red-500 p-1"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Load More Button */}
            {hasMoreDropdown && (
              <div className="p-4 text-center border-t border-gray-100">
                <button
                  onClick={loadMoreDropdownNotifications}
                  disabled={dropdownLoading}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {dropdownLoading ? (
                    <>
                      <div className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More
                      <ChevronDownIcon className="ml-1 h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => {
              setShowAll(!showAll);
              fetchNotifications(1, !showAll ? 20 : 5);
            }}
            className="w-full text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            {showAll 
              ? t('notifications.showLess', 'Show less')
              : t('notifications.showAll', 'Show all notifications')
            }
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
